import { useEffect, useState } from "react";

/**
 * The check that a person, not a script, is asking for a sign-in code.
 *
 * Drawn inside the sign-in form on all three doors. Cloudflare's script finds any
 * element with this class and renders itself into it, then puts the answer in a hidden
 * field inside the enclosing form — which is why `turnstileToken` below reads the form
 * rather than any state this component holds.
 *
 * Silent for almost everybody, and invisible with it. The widget is set to draw nothing
 * at all unless it needs an answer, so an ordinary sign-in shows no box and no tick —
 * only something that looks automated is ever asked to do anything, and only then does
 * anything appear.
 *
 * Renders nothing at all until a site key is published, so the doors work exactly as
 * they do today until the challenge is switched on.
 */
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

let cachedSiteKey: string | null | undefined;
let inFlight: Promise<string | null> | undefined;

async function loadSiteKey(): Promise<string | null> {
  if (cachedSiteKey !== undefined) return cachedSiteKey;
  // Asked of a route every host answers. This used to read the tenant bootstrap, which
  // the platform host does not serve, so the platform sign-in screen drew no check and
  // was then refused for not having one.
  inFlight ??= fetch("/api/sign-in-config", { credentials: "same-origin" })
    .then(async (response) => {
      if (!response.ok) return null;
      const config = (await response.json()) as { turnstileSiteKey?: string | null };
      return config.turnstileSiteKey ?? null;
    })
    // A key that cannot be fetched leaves the challenge undrawn. The budgets on the
    // server are still counting, and a sign-in screen nobody can use would be worse.
    .catch(() => null)
    .then((key) => {
      cachedSiteKey = key;
      return key;
    });
  return inFlight;
}

function ensureScript(): void {
  if (document.querySelector(`script[src="${SCRIPT_SRC}"]`)) return;
  const script = document.createElement("script");
  script.src = SCRIPT_SRC;
  script.async = true;
  script.defer = true;
  document.head.append(script);
}

export function TurnstileField(): React.JSX.Element | null {
  const [siteKey, setSiteKey] = useState<string | null>(cachedSiteKey ?? null);

  useEffect(() => {
    let live = true;
    void loadSiteKey().then((key) => {
      if (!live || !key) return;
      ensureScript();
      setSiteKey(key);
    });
    return () => {
      live = false;
    };
  }, []);

  if (!siteKey) return null;
  // interaction-only: the widget draws nothing unless it actually needs somebody to do
  // something. A teacher typing their own address sees no box and no tick, which is the
  // whole point of a check that is supposed to be invisible. The challenge still runs.
  return (
    <div
      className="cf-turnstile"
      data-sitekey={siteKey}
      data-action="turnstile-spin-v2"
      data-appearance="interaction-only"
    />
  );
}

/**
 * The answer, taken from the form the challenge was drawn in. Empty when the challenge
 * is switched off, which the server reads as "nothing to check".
 *
 * **A token is spent by reading it.** Cloudflare redeems each answer once and refuses it
 * ever after, and the widget does not replace it on its own. So the first press of a
 * button consumed the only answer the page had, and every later press on the same page
 * sent it again and was refused — the second attempt, not the first, is the one that
 * fails. A presenter mistyped her address, corrected it, pressed the button again, and
 * was told the check that a person is asking did not pass. Nothing was wrong with her
 * address; she was simply the second attempt.
 *
 * Asking for a fresh answer here means a retry carries a new one. It is asked for
 * immediately, so it is usually ready before anybody can press the button twice, and a
 * retry that beats it sends nothing at all — which the server reads as a challenge that
 * never ran and lets through to the budgets underneath, rather than as a refusal.
 */
export function turnstileToken(form: HTMLFormElement): string {
  const value = new FormData(form).get("cf-turnstile-response");
  refreshTurnstile();
  return typeof value === "string" ? value : "";
}

/**
 * Throws away the spent answer and asks for another.
 *
 * Deliberately silent about everything that could go wrong: the challenge may be
 * switched off, the script may never have loaded, and no widget may have been drawn.
 * None of those is a reason to break a form somebody is trying to submit.
 */
export function refreshTurnstile(): void {
  const turnstile = (globalThis as unknown as { turnstile?: { reset?: (widget?: string) => void } }).turnstile;
  try {
    turnstile?.reset?.();
  } catch {
    // A widget that will not reset leaves the field empty, which is the safe answer.
  }
}
